import React, { useRef, useEffect, ReactElement, ComponentProps, useCallback } from 'react';
import throttle from 'lodash/throttle';
import times from 'lodash/times';
import styled from 'styled-components';
import { IconForward } from '../icon/IconForward';
import { IconLeft } from '../icon/IconLeft';
import { IconButton } from '../input/IconButton';
import { Box } from '../layout/Box';

const CarouselTrackInner = styled.div`
    scroll-snap-type: x mandatory;
    display: flex;
    width: 100%;
    overflow-x: auto;
    scrollbar-width: none;

    &::-webkit-scrollbar {
        display: none;
    }

    & > * {
        min-width: 100%;
        scroll-snap-align: center;
        scroll-snap-stop: always;
        user-select: none;
    }
`;

function scrollTo(element: HTMLElement, left: number, callback: VoidFunction) {
    const onScroll = function () {
        if (Math.abs(element.scrollLeft - left) < 50) {
            element.removeEventListener('scroll', onScroll);
            callback();
        }
    };

    element.addEventListener('scroll', onScroll);
    onScroll();
    element.scrollTo({ left, behavior: 'smooth' });
}

interface CarouselProps extends ComponentProps<typeof Box> {
    count: number;
    activeIndex: number;
    renderItem: (index: number) => ReactElement | null;
    onSlideChange: (index: number) => void;
}

export const Carousel = (props: CarouselProps) => {
    const { activeIndex, onSlideChange, count, renderItem, ...other } = props;
    const scrollingRef = useRef(false);
    const carouselRef = useRef<HTMLDivElement>(null);
    const carouselTrackRef = useRef<HTMLDivElement>(null);
    const scrollIndex = useCallback(() => {
        if (!carouselRef.current || !carouselTrackRef.current) {
            return 0;
        }

        return Math.round(carouselTrackRef.current.scrollLeft / carouselRef.current.offsetWidth);
    }, [carouselRef.current, carouselTrackRef.current]);

    const onScroll = useCallback(
        throttle(() => {
            if (scrollingRef.current) {
                return;
            }

            const newIndex = scrollIndex();

            if (activeIndex !== newIndex) {
                onSlideChange(newIndex);
            }
        }, 100),
        [scrollIndex, onSlideChange, activeIndex, scrollingRef.current],
    );

    useEffect(() => {
        if (!carouselTrackRef.current || !carouselRef.current) {
            return;
        }

        scrollingRef.current = false;

        const newIndex = scrollIndex();

        if (activeIndex !== newIndex) {
            scrollingRef.current = true;

            scrollTo(
                carouselTrackRef.current,
                carouselRef.current.offsetWidth * activeIndex,
                () => (scrollingRef.current = false),
            );
        }
    }, [activeIndex, scrollIndex, carouselRef.current, carouselTrackRef.current]);

    const onSlidePrev = () => {
        if (!scrollingRef.current) {
            onSlideChange(activeIndex - 1);
        }
    };

    const onSlideNext = () => {
        if (!scrollingRef.current) {
            onSlideChange(activeIndex + 1);
        }
    };

    return (
        <Box {...other} position="relative" ref={carouselRef}>
            <CarouselTrackInner ref={carouselTrackRef} onScroll={onScroll}>
                {times(count, renderItem)}
            </CarouselTrackInner>
            {activeIndex > 0 && count > 1 && (
                <Box
                    left={1}
                    top="50%"
                    position="absolute"
                    transform="translateY(-50%)"
                    display={{ xs: 'none', md: 'block' }}
                >
                    <IconButton variant="filled" onClick={onSlidePrev}>
                        <IconLeft />
                    </IconButton>
                </Box>
            )}
            {activeIndex < count - 1 && count > 1 && (
                <Box
                    right={1}
                    top="50%"
                    position="absolute"
                    transform="translateY(-50%)"
                    display={{ xs: 'none', md: 'block' }}
                >
                    <IconButton variant="filled" onClick={onSlideNext}>
                        <IconForward />
                    </IconButton>
                </Box>
            )}
        </Box>
    );
};
